import { IntlShape } from 'react-intl';
import { buildURLWithParams } from '~/shared/utils';
import { CATALOG_URLS, MEDIATHEK_URLS } from '~/core/urls/urls';
import { Queries, Types } from '~/graphql';
import { formatTranslation, formatDate } from '~/shared/formatters';
import { NextRouter, useRouter } from 'next/router';
import { DateFormat } from 'components';
import { useMemo } from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { RoutingUtils } from '~/shared/utils/routing';

const normalize = (route: string) => {
    return route.replace(/\/\[{1,2}\.{0,3}\w+\]{1,2}/g, '/[param]');
};
export const compareRoutes = (a: string, b: string): boolean => {
    const normalizedA = normalize(a);
    const normalizedB = normalize(b);

    return normalizedA === normalizedB;
};

export type Breadcrumb = {
    label: string;
    href: string;
};
export type CacheContent = {
    contentId: string;
    city?: Types.JSON | null;
    startDateTime?: string | null;
    title?: string | null;
    contentType?: Types.ContentType | null;
    slug?: string | null;
    brand?: Types.ProductBrand | null | undefined;
};

const getBreadcrumbTitle = (content: CacheContent | null, locale: Locale) => {
    if (!content) {
        return undefined;
    }

    const parts = [
        content.title,
        content.city ? ` | ${formatTranslation(content.city, { locale })}` : '',
        content.startDateTime
            ? ` | ${formatDate(content.startDateTime, DateFormat.monthAndYearShort)}`
            : ''
    ];

    const title = parts.filter(Boolean).join('');
    return title || undefined;
};

const getBreadcrumbsFromContentType = (
    content: CacheContent | null,
    parentContent: CacheContent | null,
    formatMessage: IntlShape['formatMessage'],
    locale: Locale
) => {
    const contentType = parentContent?.contentType ?? content?.contentType;
    switch (contentType) {
        case Types.ContentType.Course:
        case Types.ContentType.OndemandCourse:
            return [
                {
                    label: formatMessage({ id: 'ondemand.handouts.landing.title' }),
                    href: CATALOG_URLS.handoutsLanding
                },
                {
                    label:
                        getBreadcrumbTitle(parentContent, locale) ??
                        formatMessage({ id: 'ondemand.handouts.landing.title' }),
                    href: buildURLWithParams(CATALOG_URLS.handoutsDetails, [
                        parentContent?.slug ??
                            parentContent?.contentId ??
                            content?.slug ??
                            content?.contentId ??
                            ''
                    ])
                }
            ];
        case Types.ContentType.Webinar:
            return [
                {
                    label: formatMessage({ id: 'common.header.link-webup' }),
                    href: MEDIATHEK_URLS.webup
                }
            ];
        case Types.ContentType.Pdf:
        default:
            return [];
    }
};

const getBreadcrumbsFromBrand = (
    content: CacheContent | null,
    parentContent: CacheContent | null,
    isMember: boolean,
    formatMessage: IntlShape['formatMessage']
) => {
    const brand = content?.brand ?? parentContent?.brand;
    if (!brand) {
        return [];
    }
    const parentSlug =
        parentContent?.slug ?? parentContent?.contentId ?? content?.slug ?? content?.contentId;
    switch (brand) {
        case Types.ProductBrand.PjTraining:
            return [
                ...(isMember
                    ? [
                          {
                              label:
                                  parentContent?.title ??
                                  formatMessage({ id: 'common.header.link-pj-begleiter' }),
                              href: buildURLWithParams(CATALOG_URLS.onDemandAccreditationCourse, [
                                  parentSlug ?? ''
                              ])
                          }
                      ]
                    : [])
            ];
        case Types.ProductBrand.Syrot:
            return isMember
                ? [
                      {
                          label:
                              parentContent?.title ??
                              formatMessage({ id: 'common.product-brand.syrot' }),
                          href: buildURLWithParams(CATALOG_URLS.syrot, [
                              parentContent?.contentId ?? ''
                          ])
                      }
                  ]
                : [];
        case Types.ProductBrand.Syfat:
            return [
                {
                    label: formatMessage({ id: 'common.header.link.syfat' }),
                    href: CATALOG_URLS.syfatLanding
                },
                ...(isMember
                    ? [
                          {
                              label:
                                  parentContent?.title ??
                                  formatMessage({ id: 'common.header.link.syfat' }),
                              href: buildURLWithParams(CATALOG_URLS.onDemandAccreditationCourse, [
                                  parentSlug ?? ''
                              ])
                          }
                      ]
                    : [])
            ];
        case Types.ProductBrand.OndemandAccredited:
            return [
                {
                    label: formatMessage({ id: 'common.header.link-cme-on-demand-course' }),
                    href: CATALOG_URLS.onDemandLanding
                },
                ...(isMember && parentSlug
                    ? [
                          {
                              label:
                                  parentContent?.title ??
                                  formatMessage({
                                      id: 'common.header.link-cme-on-demand-course'
                                  }),
                              href: buildURLWithParams(CATALOG_URLS.onDemandAccreditationCourse, [
                                  parentSlug
                              ])
                          }
                      ]
                    : [])
            ];
        case Types.ProductBrand.Guideline:
            return [
                {
                    label: formatMessage({ id: 'common.product-brand.guideline' }),
                    href: MEDIATHEK_URLS.guidelines
                }
            ];
        case Types.ProductBrand.Skill:
            return [
                {
                    label: formatMessage({ id: 'common.header.link-skills' }),
                    href: MEDIATHEK_URLS.skills
                }
            ];
        case Types.ProductBrand.Summedup:
            return [
                {
                    label: formatMessage({ id: 'common.header.link-summedup' }),
                    href: MEDIATHEK_URLS.summedup
                }
            ];
        case Types.ProductBrand.SpecialistCertificate:
            return isMember
                ? [
                      {
                          label: formatMessage({
                              id: 'common.product-brand.specialist-certificate'
                          }),
                          href: CATALOG_URLS.fachzertifikateLanding
                      },
                      ...(isMember && parentSlug
                          ? [
                                {
                                    label:
                                        parentContent?.title ??
                                        formatMessage({
                                            id: 'common.header.link-cme-on-demand-course'
                                        }),
                                    href: buildURLWithParams(
                                        CATALOG_URLS.onDemandAccreditationCourse,
                                        [parentSlug]
                                    )
                                }
                            ]
                          : [])
                  ]
                : [];
        default:
            return [];
    }
};
export const getParentBreadcrumbs = ({
    content,
    parentContent,
    isMember,
    formatMessage,
    locale
}: {
    content: CacheContent | null;
    parentContent: CacheContent | null;
    isMember: boolean;
    formatMessage: IntlShape['formatMessage'];
    locale: Locale;
}): Array<Breadcrumb> => {
    const fromBrand = getBreadcrumbsFromBrand(content, parentContent, isMember, formatMessage);
    if (fromBrand.length > 0) {
        return fromBrand;
    }
    const fromContentType = getBreadcrumbsFromContentType(
        content,
        parentContent,
        formatMessage,
        locale
    );
    return fromContentType;
};

export const extractContentIdFromQuery = (query: NextRouter['query']): string | null => {
    return (
        RoutingUtils.normalizeParam(query.contentId) ??
        RoutingUtils.normalizeParam(query.slug) ??
        RoutingUtils.normalizeParam(query.id) ??
        null
    );
};

export const useGetBreadcrumbsContentIdFromQuery = (contentUrls: Array<string>) => {
    const router = useRouter();

    const contentId = useMemo(() => {
        if (contentUrls.some((url) => compareRoutes(url, router.pathname))) {
            const contentId = extractContentIdFromQuery(router.query);
            if (contentId) {
                return contentId;
            }
        }
        return null;
    }, [router.query, router.pathname, contentUrls]);

    return contentId;
};

export const useGetBreadcrumbsData = (contentId: string | null) => {
    const { data: contentFirstData } = Queries.useGetContentByContentIdForBreadcrumbsQuery({
        variables: { contentId: contentId ?? '' },
        fetchPolicy: 'cache-first',
        skip: !contentId,
        errorPolicy: 'ignore'
    });

    const { data: courseFirstData } = Queries.useGetCourseByIdForBreadcrumbsQuery({
        variables: { identifier: contentId ?? '' },
        fetchPolicy: 'cache-first',
        skip: !contentId,
        errorPolicy: 'ignore'
    });

    const firstData = useMemo(() => {
        return contentFirstData?.content ?? courseFirstData?.course;
    }, [contentFirstData, courseFirstData]);

    const secondContentId = useMemo(() => {
        if (firstData?.parent?.contentId) {
            return firstData.parent.contentId;
        }
        return null;
    }, [firstData]);

    const { data: contentSecondData, previousData: previousContentSecondData } =
        Queries.useGetContentByContentIdForBreadcrumbsQuery({
            variables: { contentId: secondContentId ?? '' },
            fetchPolicy: 'cache-first',
            skip: !secondContentId,
            errorPolicy: 'ignore'
        });
    const { data: courseSecondData, previousData: previousCourseSecondData } =
        Queries.useGetCourseByIdForBreadcrumbsQuery({
            variables: { identifier: secondContentId ?? '' },
            fetchPolicy: 'cache-first',
            skip: !secondContentId,
            errorPolicy: 'ignore'
        });

    const { parentDataToUse, dataToUse } = (() => {
        const secondData =
            contentSecondData?.content ??
            courseSecondData?.course ??
            previousContentSecondData?.content ??
            previousCourseSecondData?.course;

        if (firstData?.parent == null) {
            return {
                parentDataToUse: firstData,
                dataToUse: null
            };
        }
        return {
            parentDataToUse: secondData,
            dataToUse: firstData
        };
    })();

    return { data: dataToUse, parentData: parentDataToUse };
};

export const getSsrBreadcrumbsData = (
    client: ApolloClient<NormalizedCacheObject>,
    contentId: string,
    kind?: 'course' | 'content'
) => {
    const promises = [
        ...(kind === 'content'
            ? [
                  client.query<
                      Queries.GetContentByContentIdForBreadcrumbsQuery | null,
                      Queries.GetContentByContentIdForBreadcrumbsQueryVariables
                  >({
                      query: Queries.GetContentByContentIdForBreadcrumbs,
                      variables: {
                          contentId
                      },
                      errorPolicy: 'ignore'
                  })
              ]
            : []),
        ...(kind === 'course'
            ? [
                  client.query<
                      Queries.GetCourseByIdForBreadcrumbsQuery | null,
                      Queries.GetCourseByIdForBreadcrumbsQueryVariables
                  >({
                      query: Queries.GetCourseByIdForBreadcrumbs,
                      variables: {
                          identifier: contentId
                      },
                      errorPolicy: 'ignore'
                  })
              ]
            : [])
    ];

    return Promise.all(promises);
};
